/**
 * Error Collector
 * Collects JavaScript errors, resource errors, and unhandled promise rejections
 */

import { BaseCollector } from './BaseCollector.js';
import type { EventBus } from '../core/EventBus.js';
import type { BreadcrumbManager } from '../utils/session.js';
import { createErrorEvent } from '../models/ErrorEvent.js';
import { SeverityClassifier } from '../strategies/severity/index.js';

export class ErrorCollector extends BaseCollector {
  private breadcrumbManager: BreadcrumbManager;
  private severityClassifier: SeverityClassifier;
  private sendCriticalErrorsImmediately: boolean;
  private isHandlingInternalError: boolean = false;

  // Event listeners for cleanup
  private errorListener: ((event: ErrorEvent) => void) | null = null;
  private resourceErrorListener: ((event: Event) => void) | null = null;
  private rejectionListener: ((event: PromiseRejectionEvent) => void) | null = null;

  constructor(
    eventBus: EventBus,
    sessionId: string,
    userId: string | null,
    breadcrumbManager: BreadcrumbManager,
    config: {
      sendCriticalErrorsImmediately: boolean;
      debug: boolean;
    }
  ) {
    super('ErrorCollector', eventBus, sessionId, userId, config.debug);
    this.breadcrumbManager = breadcrumbManager;
    this.severityClassifier = new SeverityClassifier();
    this.sendCriticalErrorsImmediately = config.sendCriticalErrorsImmediately;
  }

  initialize(): void {
    this.log('Initializing error tracking');

    // JavaScript errors
    this.errorListener = (event: ErrorEvent) => {
      if (this.isHandlingInternalError) return;

      // Check if this error is from watchdog itself
      const errorMessage = event.message || '';
      const errorFilename = event.filename || '';
      if (errorMessage.includes('[Watchdog]') || errorMessage.includes('[Analytics]') ||
          errorFilename.includes('watchdog') || errorFilename.includes('analytics')) {
        this.log('Internal error detected, skipping to prevent loop');
        return;
      }

      this.isHandlingInternalError = true;

      try {
        const baseMetadata = this.getBaseMetadata();
        const severity = this.severityClassifier.classify({
          type: 'javascript',
          error: event.error || event.message,
          stack: event.error?.stack
        });

        const errorEvent = createErrorEvent(
          baseMetadata,
          event.error || event.message,
          'javascript_error',
          severity,
          this.breadcrumbManager.getAll(),
          {
            filename: event.filename,
            lineno: event.lineno,
            colno: event.colno
          }
        );

        this.emit('event:collected', errorEvent);
        this.breadcrumbManager.add('error', errorEvent.error_message, { severity });

        // Emit critical error for immediate sending if configured
        if (this.sendCriticalErrorsImmediately && (severity === 'critical' || severity === 'high')) {
          this.emit('error:critical', errorEvent);
        }

        this.log(`Collected JavaScript error: ${errorEvent.error_message} (${severity})`);
      } catch (error) {
        this.error('Failed to track JavaScript error:', error);
      } finally {
        this.isHandlingInternalError = false;
      }
    };

    window.addEventListener('error', this.errorListener);

    // Resource errors (images, scripts, stylesheets)
    this.resourceErrorListener = (event: Event) => {
      if (this.isHandlingInternalError) return;

      const target = event.target as HTMLElement;
      if (target && target !== (window as any)) {
        this.isHandlingInternalError = true;

        try {
          let resourceUrl = '';
          let resourceType = '';

          if (target instanceof HTMLScriptElement) {
            resourceUrl = target.src;
            resourceType = 'script';
          } else if (target instanceof HTMLLinkElement) {
            resourceUrl = target.href;
            resourceType = 'stylesheet';
          } else if (target instanceof HTMLImageElement) {
            resourceUrl = target.src;
            resourceType = 'image';
          }

          if (resourceUrl) {
            const baseMetadata = this.getBaseMetadata();
            const message = `Failed to load ${resourceType}: ${resourceUrl}`;
            const severity = this.severityClassifier.classify({
              type: 'resource',
              error: message,
              url: resourceUrl
            });

            const errorEvent = createErrorEvent(
              baseMetadata,
              message,
              'resource_error',
              severity,
              this.breadcrumbManager.getAll(),
              {
                resourceUrl,
                resourceType
              }
            );

            this.emit('event:collected', errorEvent);
            this.breadcrumbManager.add('error', message);

            if (this.sendCriticalErrorsImmediately && (severity === 'critical' || severity === 'high')) {
              this.emit('error:critical', errorEvent);
            }

            this.log(`Collected resource error: ${resourceType} (${severity})`);
          }
        } catch (error) {
          this.error('Failed to track resource error:', error);
        } finally {
          this.isHandlingInternalError = false;
        }
      }
    };

    window.addEventListener('error', this.resourceErrorListener, true);

    // Promise rejections
    this.rejectionListener = (event: PromiseRejectionEvent) => {
      if (this.isHandlingInternalError) return;

      this.isHandlingInternalError = true;

      try {
        const reason = event.reason;
        let message = 'Unhandled promise rejection';
        let error: Error | undefined;

        if (reason instanceof Error) {
          message = reason.message;
          error = reason;
        } else if (typeof reason === 'string') {
          message = reason;
        } else if (reason && typeof reason === 'object') {
          message = JSON.stringify(reason);
        }

        // Check if this error is from watchdog itself
        if (message.includes('[Watchdog]') || message.includes('[Analytics]')) {
          this.log('Internal promise rejection detected, skipping');
          return;
        }

        const baseMetadata = this.getBaseMetadata();
        const severity = this.severityClassifier.classify({
          type: 'promise',
          error: error || message,
          stack: error?.stack
        });

        const errorEvent = createErrorEvent(
          baseMetadata,
          error || message,
          'promise_rejection',
          severity,
          this.breadcrumbManager.getAll()
        );

        this.emit('event:collected', errorEvent);
        this.breadcrumbManager.add('error', errorEvent.error_message, { severity });

        if (this.sendCriticalErrorsImmediately && (severity === 'critical' || severity === 'high')) {
          this.emit('error:critical', errorEvent);
        }

        this.log(`Collected promise rejection: ${message} (${severity})`);
      } catch (error) {
        this.error('Failed to track promise rejection:', error);
      } finally {
        this.isHandlingInternalError = false;
      }
    };

    window.addEventListener('unhandledrejection', this.rejectionListener);

    this.isInitialized = true;
  }

  destroy(): void {
    if (this.errorListener) {
      window.removeEventListener('error', this.errorListener);
      this.errorListener = null;
    }

    if (this.resourceErrorListener) {
      window.removeEventListener('error', this.resourceErrorListener, true);
      this.resourceErrorListener = null;
    }

    if (this.rejectionListener) {
      window.removeEventListener('unhandledrejection', this.rejectionListener);
      this.rejectionListener = null;
    }

    this.isInitialized = false;
    this.log('Destroyed');
  }
}

